/** PRESCURTĂRILE ȘI TERMENII panourilor de costuri, cu explicația la hover.
 *
 *  Textele vin fie din catalog (`t`), fie gata scrise de server (rândurile
 *  rezumatului de business case, explicațiile programului); amândouă poartă
 *  aceleași prescurtări — TIC, ROI, GBA, CUT, P.U., lag… — pe care un cititor
 *  nou nu le știe. În loc să fie explicate la fiecare loc, se RECUNOSC în text
 *  și primesc cartonașul de explicație al platformei (`HoverCard`), din
 *  glosarul `termen.*` al catalogului, în limba ecranului. Un text fără
 *  termeni iese neatins; un jeton se recunoaște numai întreg (limite de
 *  cuvânt), deci «CUTIE» nu e «CUT». */
import { Fragment, type ReactNode } from "react";
import HoverCard from "../../structure/HoverCard";
import { useT, type Cheie } from "../../i18n";

/** jetonul așa cum apare în text, în română sau în engleză → termenul din glosar */
const JETOANE: Record<string, string> = {
  TIC: "tic", ROI: "roi",
  GBA: "gba", GFA: "gba", SCD: "scd", ADC: "adc", Su: "su",
  CUT: "cut", POT: "pot", PUZ: "puz", RLU: "rlu",
  CC: "cc", ISC: "isc", TVA: "tva", VAT: "tva",
  XPS: "xps", PSI: "psi", RF: "rf",
  "P.U.": "pu", "Unit price": "pu", "Unit rate": "pu",
  "U.M.": "um", Unit: "um",
  lag: "lag", Lag: "lag", zl: "zl",
  "luni-macara": "luni_macara", "crane-months": "luni_macara",
  "ore-om": "ore_om", "Ore-om": "ore_om", "man-hours": "ore_om", "Man-hours": "ore_om",
  "fronturi de structură": "front", "front de structură": "front",
  "structure fronts": "front", "structure front": "front",
  "plafon": "plafon", "cap": "plafon",
  "preț de echilibru": "echilibru", "Preț de echilibru": "echilibru",
  "Break-even": "echilibru", "break-even": "echilibru",
  "Marjă brută": "marja", "marjă brută": "marja", "Gross margin": "marja", "gross margin": "marja",
  "lanțul critic": "lant_critic", "Lanțul critic": "lant_critic",
  RIR: "rir", IRR: "rir", "capital-ani": "capital_ani", "capital-years": "capital_ani",
  "critical chain": "lant_critic", "Critical chain": "lant_critic", "critical path": "lant_critic",
};

// cratima nu e specială în afara unei clase de caractere, iar în modul Unicode
// un «\-» scăpat e o greșeală de sintaxă
const scapa = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
// cele mai lungi întâi, ca «P.U.» să nu fie prins ca «P» și «luni-macara» întreg
const MODEL = `(?<![\\p{L}\\p{N}])(${Object.keys(JETOANE).sort((a, b) => b.length - a.length)
  .map(scapa).join("|")})(?![\\p{L}\\p{N}])`;

/** Un termen explicat: declanșatorul e textul dat (eticheta unui card, o
 *  prescurtare din text), cartonașul e numele întreg și explicația lui. */
export function Termen({ termen, children }: { termen: string; children: ReactNode }) {
  const t = useT();
  const nume = t(`termen.${termen}.nume` as Cheie);
  return (
    <HoverCard clasaAncora="termen" latime={380} eticheta={t("termen.explicatie", { ce: nume })}
               continut={<>
                 <span className="fx-tip-title">{nume}</span>
                 <span>{t(`termen.${termen}.ce` as Cheie)}</span>
               </>}>
      {children}
    </HoverCard>
  );
}

/** Textul cu termenii recunoscuți înveliți în `Termen`; altceva decât un
 *  text (un nod gata desenat) trece neatins. */
export default function Termeni({ text }: { text: ReactNode }) {
  if (typeof text !== "string" || !text) return <>{text}</>;
  const re = new RegExp(MODEL, "gu");
  const bucati: ReactNode[] = [];
  let ultim = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > ultim) bucati.push(text.slice(ultim, m.index));
    bucati.push(<Termen termen={JETOANE[m[1]]}>{m[1]}</Termen>);
    ultim = m.index + m[0].length;
  }
  if (!bucati.length) return <>{text}</>;
  if (ultim < text.length) bucati.push(text.slice(ultim));
  return <>{bucati.map((b, i) => <Fragment key={i}>{b}</Fragment>)}</>;
}
